import { useState, useEffect, useRef, useCallback } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { api } from '../api';

/* ── Types ─────────────────────────────────────────────────────────── */

interface Stop { label: string; postcode: string; address: string; waiting_time_hours: number }
interface Customer {
  id: number; customer_name: string; contact_name?: string; email?: string;
  phone?: string; address?: string; postcode?: string; notes?: string;
  accounts_contact_name?: string; accounts_contact_email?: string;
  accounts_contact_phone?: string; invoicing_contact_name?: string;
  invoicing_contact_email?: string; invoicing_contact_phone?: string;
  credit_terms_days?: number; total_spend?: number; job_count?: number;
  outstanding_amount?: number; quote_count?: number;
}
interface CalcResult {
  stops: any[]; legs: any[]; route_geometry: number[][]; journey_plan: any;
  costs: any; rate_card_benchmark: any; variance: any; inputs: any;
  summary: any; has_ors_route: boolean; ors_key_configured: boolean;
}
interface RateCard {
  tractor_trailer_day: number; power_only_day: number;
  waiting_per_hour: number; waiting_included_hours: number;
  weekend_surcharge_pct: number;
  touring_day_low: number; touring_day_high: number;
  touring_week_low: number; touring_week_high: number;
  touring_overnight_low: number; touring_overnight_high: number;
  drop_collect_drop: number; drop_collect_collection: number;
  drop_collect_stand_day: number;
}

const DEFAULT_RATE_CARD: RateCard = {
  tractor_trailer_day: 595, power_only_day: 395,
  waiting_per_hour: 45, waiting_included_hours: 2,
  weekend_surcharge_pct: 15,
  touring_day_low: 675, touring_day_high: 875,
  touring_week_low: 3500, touring_week_high: 5250,
  touring_overnight_low: 825, touring_overnight_high: 1050,
  drop_collect_drop: 295, drop_collect_collection: 295,
  drop_collect_stand_day: 125,
};

/* ── Leaflet CDN loader ───────────────────────────────────────────── */

let leafletLoaded = false;
function loadLeaflet(): Promise<void> {
  if (leafletLoaded) return Promise.resolve();
  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => { leafletLoaded = true; resolve(); };
    document.head.appendChild(script);
  });
}

/* ── Formatting helpers ───────────────────────────────────────────── */

const fmt = (n: number | null | undefined) => n != null ? `£${n.toFixed(2)}` : '—';
const fmtMiles = (n: number | null | undefined) => n != null ? `${n.toFixed(1)} mi` : '—';
const fmtHours = (h: number | null | undefined) => {
  if (h == null) return '—';
  const hrs = Math.floor(h); const mins = Math.round((h - hrs) * 60);
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
};
const timeStr = (h: number) => {
  const hrs = Math.floor(h) % 24;
  const mins = Math.round((h - Math.floor(h)) * 60);
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};
const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700', declined: 'bg-red-100 text-red-700',
  expired: 'bg-yellow-100 text-yellow-700', converted: 'bg-purple-100 text-purple-700',
};
function errorMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  return typeof e === 'string' ? e : String(e);
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════ */

export function QuotePage() {
  const [tab, setTab] = useState<'calculator' | 'quotes' | 'customers' | 'settings'>('calculator');
  const tabs = [
    { id: 'calculator', label: 'New Quote' },
    { id: 'quotes', label: 'Quotes' },
    { id: 'customers', label: 'Customers' },
    { id: 'settings', label: 'Settings' },
  ] as const;

  return (
    <PortalLayout currentPage="quote">
      <div className="p-5">
        <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900 mb-1">Quotes</h1>
        <p className="text-gray-500 text-sm mb-6">Cost-based journey calculator with EC 561 driving hours, WTD & rate card benchmarking</p>
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>{t.label}</button>
          ))}
        </div>
        {tab === 'calculator' && <CalculatorTab />}
        {tab === 'quotes' && <QuotesTab />}
        {tab === 'customers' && <CustomersTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </PortalLayout>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CALCULATOR TAB — Cost-based pricing with rate card benchmark
   ═══════════════════════════════════════════════════════════════════ */

function CalculatorTab() {
  // Mode selector: haulage or coach
  const [mode, setMode] = useState<'haulage' | 'coach'>('haulage');

  // Stops
  const [stops, setStops] = useState<Stop[]>([
    { label: 'Depot', postcode: '', address: '', waiting_time_hours: 0 },
    { label: 'Pickup', postcode: '', address: '', waiting_time_hours: 1 },
    { label: 'Destination', postcode: '', address: '', waiting_time_hours: 1 },
  ]);
  const [returnToStart, setReturnToStart] = useState(true);

  // Coach-specific: pick-up round (passenger collections before main journey)
  const [pickupRound, setPickupRound] = useState<Stop[]>([]);
  const addPickupStop = () => setPickupRound(prev => [...prev, { label: '', postcode: '', address: '', waiting_time_hours: 0.5 }]);
  const removePickupStop = (idx: number) => setPickupRound(prev => prev.filter((_, i) => i !== idx));
  const updatePickupStop = (idx: number, field: keyof Stop, val: any) => {
    setPickupRound(prev => { const ns = [...prev]; ns[idx] = { ...ns[idx], [field]: val }; return ns; });
  };

  // Movement type (for rate card benchmarking)
  const [serviceType, setServiceType] = useState('standard_ops');
  const [serviceSubtype, setServiceSubtype] = useState('tractor_trailer');
  const [isWeekend, setIsWeekend] = useState(false);
  const [standDays, setStandDays] = useState(0);
  const [touringRate, setTouringRate] = useState(0);

  // Coach-specific fields
  const [vehicleClass, setVehicleClass] = useState('standard_coach');
  const [passengerCount, setPassengerCount] = useState<number | ''>('');
  const [wheelchairAccessible, setWheelchairAccessible] = useState(false);
  const [wheelchairPositions, setWheelchairPositions] = useState<number | ''>('');
  const [lowFloorAccess, setLowFloorAccess] = useState(false);
  const [coachServiceType, setCoachServiceType] = useState('private_hire');
  const [pricingMode, setPricingMode] = useState<'cost_plus' | 'fixed' | 'rate_card' | 'per_mile' | 'per_hour'>('cost_plus');
  const [fixedPrice, setFixedPrice] = useState<number | ''>('');
  const [perMilePrice, setPerMilePrice] = useState<number | ''>('');
  const [perHourPrice, setPerHourPrice] = useState<number | ''>('');
  const [quoteValidityDays, setQuoteValidityDays] = useState<number | ''>(14);
  const [customTermsAndConditions, setCustomTermsAndConditions] = useState('');

  // Cost inputs (these drive the primary quote price)
  const [vehicleType, setVehicleType] = useState('hgv');
  const [mpg, setMpg] = useState<number | ''>('');
  const [fuelPrice, setFuelPrice] = useState<number | ''>('');
  const [driverRate, setDriverRate] = useState<number | ''>('');
  const [marginPct, setMarginPct] = useState<number | ''>(27.5);
  const [runningCostPerMile, setRunningCostPerMile] = useState<number | ''>(0.30);
  const [extraCosts, setExtraCosts] = useState<number | ''>(0);
  const [extraDesc, setExtraDesc] = useState('');
  const [includeVat, setIncludeVat] = useState(true);

  // Customer
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  // State
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState<CalcResult | null>(null);
  const [rateCard, setRateCard] = useState<RateCard>(DEFAULT_RATE_CARD);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);

  // Load defaults
  useEffect(() => {
    (async () => {
      try {
        const [config, fp, custs] = await Promise.all([
          api.getQuoteConfig(), api.getLatestFuelPrice(), api.getCustomers(),
        ]);
        if (config.depot_postcode) {
          setStops(prev => {
            const ns = [...prev];
            if (ns[0] && !ns[0].postcode) ns[0].postcode = config.depot_postcode;
            return ns;
          });
        }
        setMpg(vehicleType === 'hgv' ? config.default_mpg_hgv : config.default_mpg_van);
        setDriverRate(config.default_driver_rate);
        setMarginPct(config.default_profit_margin || 27.5);
        setRunningCostPerMile(config.default_running_cost_per_mile || 0.30);
        if (fp.price_per_litre) setFuelPrice(fp.price_per_litre);
        if (config.rate_card) setRateCard({ ...DEFAULT_RATE_CARD, ...config.rate_card });
        setCustomers(custs);
      } catch {}
    })();
  }, []);

  const updateStop = (idx: number, field: keyof Stop, val: any) => {
    setStops(prev => { const ns = [...prev]; ns[idx] = { ...ns[idx], [field]: val }; return ns; });
  };
  const addStop = () => setStops(prev => [...prev, { label: '', postcode: '', address: '', waiting_time_hours: 0.5 }]);
  const removeStop = (idx: number) => { if (stops.length > 2) setStops(prev => prev.filter((_, i) => i !== idx)); };

  const calculate = async () => {
    setError(''); setResult(null); setSaveMsg('');
    const badStop = stops.find(s => !s.postcode.trim() && !s.address.trim());
    if (badStop) { setError('Please fill in a postcode or address for every stop'); return; }
    setCalculating(true);
    try {
      const res = await api.calculateQuote({
        stops: stops.map(s => ({ label: s.label, postcode: s.postcode, address: s.address, waiting_time_hours: s.waiting_time_hours })),
        vehicle_type: vehicleType,
        mpg: mpg || undefined,
        fuel_price_per_litre: fuelPrice || undefined,
        driver_rate_per_hour: driverRate || undefined,
        profit_margin_pct: marginPct || 27.5,
        running_cost_per_mile: runningCostPerMile || 0.30,
        extra_costs: (extraCosts as number) || 0,
        include_vat: includeVat,
        return_to_start: returnToStart,
        service_type: serviceType,
        service_subtype: serviceSubtype,
        is_weekend: isWeekend,
        stand_days: standDays,
        touring_rate: touringRate || undefined,
      });
      setResult(res);
      renderMap(res);
    } catch (e) { setError(errorMsg(e)); }
    finally { setCalculating(false); }
  };

  const renderMap = useCallback(async (res: CalcResult) => {
    await loadLeaflet();
    const L = (window as any).L;
    if (!L || !mapRef.current) return;
    if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }
    const map = L.map(mapRef.current).setView([54.5, -2], 6);
    mapInstance.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);
    const bounds: any[] = [];
    res.stops.forEach((s: any, i: number) => {
      const color = i === 0 ? '#16a34a' : i === res.stops.length - 1 ? '#dc2626' : '#2563eb';
      L.circleMarker([s.latitude, s.longitude], {
        radius: 8, fillColor: color, color: '#fff', weight: 2, fillOpacity: 0.9,
      }).addTo(map).bindPopup(`<b>${s.label || `Stop ${i + 1}`}</b><br/>${s.postcode || ''}<br/>${s.admin_district || ''}`);
      bounds.push([s.latitude, s.longitude]);
    });
    if (res.route_geometry?.length > 0) L.polyline(res.route_geometry, { color: '#4f46e5', weight: 4, opacity: 0.7 }).addTo(map);
    if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
  }, []);

  const saveQuote = async () => {
    if (!result) return;
    setSaving(true); setSaveMsg('');
    try {
      const c = result.costs;
      const notes = mode === 'coach'
        ? `Coach: ${vehicleClass} | Service: ${coachServiceType} | Passengers: ${passengerCount || '—'} | Wheelchair: ${wheelchairAccessible ? 'Yes' : 'No'}`
        : `Service: ${serviceType}/${serviceSubtype} | Fuel surcharge: ${c.fuel_surcharge_pct}%`;

      const res = await api.saveQuote({
        customer_id: customerId,
        vehicle_type: mode === 'coach' ? vehicleClass : vehicleType,
        mode: mode,
        stops: stops.map(s => ({ label: s.label, postcode: s.postcode, address: s.address, waiting_time_hours: s.waiting_time_hours })),
        mpg: result.inputs.mpg,
        fuel_price_per_litre: result.inputs.fuel_price_per_litre,
        driver_rate_per_hour: result.inputs.driver_rate_per_hour,
        profit_margin_pct: result.inputs.profit_margin_pct,
        extra_costs: (extraCosts as number) || 0,
        extra_costs_description: extraDesc || null,
        include_vat: includeVat,
        return_to_start: returnToStart,
        total_distance_miles: result.summary.total_distance_miles,
        total_driving_hours: result.summary.total_driving_hours,
        total_duty_hours: result.summary.total_duty_hours,
        overnight_count: result.summary.overnight_count,
        fuel_cost: c.fuel_cost_at_current,
        driver_cost: c.driver_cost,
        overnight_cost: c.overnight_cost,
        subtotal: c.base_costs,
        margin_amount: c.margin_amount,
        vat_amount: c.vat_amount,
        total_price: c.total_price,
        journey_plan_json: JSON.stringify(result.journey_plan),
        route_geometry_json: JSON.stringify(result.route_geometry),
        pricing_mode: pricingMode,
        quote_validity_days: quoteValidityDays || 14,
        custom_terms: customTermsAndConditions || null,
        coach_service_type: mode === 'coach' ? coachServiceType : null,
        passenger_count: mode === 'coach' ? (passengerCount as number) : null,
        wheelchair_accessible: mode === 'coach' ? wheelchairAccessible : false,
        wheelchair_positions: mode === 'coach' ? (wheelchairPositions as number) : null,
        pickup_round: mode === 'coach' ? pickupRound : [],
        notes: notes,
      });
      setSaveMsg(`Saved as ${res.quote_number}`);
    } catch (e) { setSaveMsg(`Error: ${errorMsg(e)}`); }
    finally { setSaving(false); }
  };

  // Service type sub-options (for benchmarking)
  const serviceSubtypes: Record<string, { value: string; label: string }[]> = {
    standard_ops: [
      { value: 'tractor_trailer', label: `Tractor & Trailer (${fmt(rateCard.tractor_trailer_day)}/day)` },
      { value: 'power_only', label: `Power Only (${fmt(rateCard.power_only_day)}/day)` },
    ],
    touring: [
      { value: 'touring_day', label: `Day Rate (${fmt(rateCard.touring_day_low)}–${fmt(rateCard.touring_day_high)})` },
      { value: 'touring_week', label: `Weekly Package (${fmt(rateCard.touring_week_low)}–${fmt(rateCard.touring_week_high)})` },
      { value: 'touring_overnight', label: `Overnight Move (${fmt(rateCard.touring_overnight_low)}–${fmt(rateCard.touring_overnight_high)})` },
    ],
    drop_collect: [
      { value: 'drop_collect', label: `Drop ${fmt(rateCard.drop_collect_drop)} + Collection ${fmt(rateCard.drop_collect_collection)}` },
    ],
  };

  const c = result?.costs;
  const bench = result?.rate_card_benchmark;
  const variance = result?.variance;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      {/* ── LEFT: Inputs ── */}
      <div className="space-y-4">
        {/* Mode Selector */}
        <div className="bg-slate-50 rounded border border-slate-200 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-3">Quote Mode</h3>
          <div className="flex gap-2">
            <button onClick={() => setMode('haulage')}
              className={`flex-1 py-2 rounded text-sm font-medium border-2 transition-colors ${
                mode === 'haulage' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}>Haulage</button>
            <button onClick={() => setMode('coach')}
              className={`flex-1 py-2 rounded text-sm font-medium border-2 transition-colors ${
                mode === 'coach' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}>Coach/PSV</button>
          </div>
        </div>

        {/* Customer */}
        <div className="bg-white rounded border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Customer</h3>
          <div className="flex gap-2">
            <select className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
              value={customerId ?? ''} onChange={e => setCustomerId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">— No customer —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.customer_name}</option>)}
            </select>
            <button onClick={() => setShowCustomerModal(true)} className="px-3 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-800">+ New</button>
          </div>
        </div>

        {/* Movement Type (for rate card benchmark) — HAULAGE ONLY */}
        {mode === 'haulage' && (
          <div className="bg-white rounded border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Movement Type</h3>
            <p className="text-xs text-gray-400 mb-3">Select to benchmark against your published rate card</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                { v: 'standard_ops', l: 'Standard Operations' },
                { v: 'touring', l: 'Touring & Events' },
                { v: 'drop_collect', l: 'Drop & Collect' },
              ].map(o => (
                <button key={o.v} onClick={() => { setServiceType(o.v); setServiceSubtype(serviceSubtypes[o.v]?.[0]?.value || ''); }}
                  className={`px-3 py-2 rounded text-xs font-medium border transition-colors ${
                    serviceType === o.v ? 'bg-slate-700 text-white border-blue-500' : 'bg-white text-gray-600 border-gray-300 hover:border-slate-400'
                  }`}>{o.l}</button>
              ))}
            </div>
            <select className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3"
              value={serviceSubtype} onChange={e => setServiceSubtype(e.target.value)}>
              {(serviceSubtypes[serviceType] || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={isWeekend} onChange={e => setIsWeekend(e.target.checked)} className="rounded" />
                Weekend / Bank Holiday (+{rateCard.weekend_surcharge_pct}%)
              </label>
              {serviceType === 'drop_collect' && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Stand days:</label>
                  <input type="number" min="0" value={standDays} onChange={e => setStandDays(parseInt(e.target.value) || 0)}
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm" />
                </div>
              )}
              {serviceType === 'touring' && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Rate (£):</label>
                  <input type="number" step="25" value={touringRate || ''} onChange={e => setTouringRate(parseFloat(e.target.value) || 0)}
                    placeholder="Pick within range" className="w-32 border border-gray-300 rounded px-2 py-1 text-sm" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Coach-specific fields */}
        {mode === 'coach' && (
          <div className="bg-white rounded border border-gray-200 p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Coach/PSV Details</h3>

            {/* Vehicle Class */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">Vehicle Class</label>
              <select value={vehicleClass} onChange={e => setVehicleClass(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                <option value="minibus">Minibus (8-16 seats)</option>
                <option value="minicoach">Minicoach (16 seats)</option>
                <option value="midi">Midi Coach (29 seats)</option>
                <option value="standard_coach">Standard Coach (49 seats)</option>
                <option value="executive">Executive Coach (49 seats)</option>
                <option value="double_decker">Double Decker (70+ seats)</option>
              </select>
            </div>

            {/* Passenger Count */}
            <div>
              <label className="text-xs text-gray-500 block mb-1">Passenger Count</label>
              <input type="number" min="0" value={passengerCount} onChange={e => setPassengerCount(e.target.value ? parseInt(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>

            {/* Accessibility */}
            <div className="border-t pt-3">
              <label className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <input type="checkbox" checked={wheelchairAccessible} onChange={e => setWheelchairAccessible(e.target.checked)} className="rounded" />
                Wheelchair Accessible
              </label>
              {wheelchairAccessible && (
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Wheelchair Positions</label>
                  <input type="number" min="0" value={wheelchairPositions} onChange={e => setWheelchairPositions(e.target.value ? parseInt(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                <input type="checkbox" checked={lowFloorAccess} onChange={e => setLowFloorAccess(e.target.checked)} className="rounded" />
                Low Floor Access Required
              </label>
            </div>

            {/* Service Type for Coach */}
            <div className="border-t pt-3">
              <label className="text-xs text-gray-500 block mb-1">Service Type</label>
              <select value={coachServiceType} onChange={e => setCoachServiceType(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                <option value="private_hire">Private Hire</option>
                <option value="contract">Contract Service</option>
                <option value="school_run">School Run</option>
                <option value="rail_replacement">Rail Replacement</option>
                <option value="corporate">Corporate Shuttle</option>
                <option value="tour">Tour/Excursion</option>
                <option value="airport">Airport Transfer</option>
                <option value="wedding">Wedding</option>
              </select>
            </div>
          </div>
        )}

        {/* Pick-up Round — COACH ONLY */}
        {mode === 'coach' && (
          <div className="bg-white rounded border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">Pick-up Round</h3>
                <p className="text-xs text-gray-400">Collection points before main journey</p>
              </div>
              <button onClick={addPickupStop} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200">+ Add Pick-up</button>
            </div>
            <div className="space-y-3">
              {pickupRound.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No pick-up points added</p>
              ) : (
                pickupRound.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 p-3 bg-gray-50 rounded">
                    <div className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1 bg-purple-500">P{i + 1}</div>
                    <div className="flex-1 space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        <input placeholder="Location" value={s.label} onChange={e => updatePickupStop(i, 'label', e.target.value)}
                          className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                        <input placeholder="Postcode" value={s.postcode}
                          onChange={e => updatePickupStop(i, 'postcode', e.target.value.toUpperCase())}
                          className="border border-gray-300 rounded px-2 py-1.5 text-sm font-mono" />
                        <div className="flex items-center gap-1">
                          <input type="time" value={s.waiting_time_hours ? String(s.waiting_time_hours) : ''} placeholder="Pick-up time"
                            onChange={e => updatePickupStop(i, 'waiting_time_hours', e.target.value ? parseFloat(e.target.value) : 0)}
                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                        </div>
                      </div>
                    </div>
                    {pickupRound.length > 0 && (
                      <button onClick={() => removePickupStop(i)} className="text-gray-400 hover:text-red-500 mt-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Stops */}
        <div className="bg-white rounded border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">{mode === 'coach' ? 'Main Destination' : 'Route Stops'}</h3>
            <button onClick={addStop} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200">+ Add Stop</button>
          </div>
          <div className="space-y-3">
            {stops.map((s, i) => (
              <div key={i} className="flex items-start gap-2 p-3 bg-gray-50 rounded">
                <div className={`w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1 ${
                  i === 0 ? 'bg-green-500' : i === stops.length - 1 ? 'bg-red-500' : 'bg-blue-500'
                }`}>{i + 1}</div>
                <div className="flex-1 space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <input placeholder="Label" value={s.label} onChange={e => updateStop(i, 'label', e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
                    <input placeholder="UK Postcode" value={s.postcode}
                      onChange={e => updateStop(i, 'postcode', e.target.value.toUpperCase())}
                      className="border border-gray-300 rounded px-2 py-1.5 text-sm font-mono" />
                    <div className="flex items-center gap-1">
                      <input type="number" step="0.5" min="0" placeholder="Wait" value={s.waiting_time_hours || ''}
                        onChange={e => updateStop(i, 'waiting_time_hours', parseFloat(e.target.value) || 0)}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" />
                      <span className="text-xs text-gray-400">hrs</span>
                    </div>
                  </div>
                  <input placeholder="Or full address (for EU/international)" value={s.address}
                    onChange={e => updateStop(i, 'address', e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-500" />
                </div>
                {stops.length > 2 && i > 0 && (
                  <button onClick={() => removeStop(i)} className="text-gray-400 hover:text-red-500 mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm text-gray-600">
            <input type="checkbox" checked={returnToStart} onChange={e => setReturnToStart(e.target.checked)} className="rounded" />
            Return to depot
          </label>
        </div>

        {/* Pricing Mode Selector */}
        <div className="bg-white rounded border border-gray-200 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700 mb-3">Pricing Method</h3>
          <div className="space-y-2">
            {[
              { v: 'cost_plus' as const, l: 'Cost Plus Margin' },
              { v: 'fixed' as const, l: 'Fixed Price' },
              { v: 'per_mile' as const, l: 'Per Mile Rate' },
              { v: 'per_hour' as const, l: 'Per Hour Rate' },
            ].map(o => (
              <label key={o.v} className="flex items-center gap-2 text-sm text-gray-600">
                <input type="radio" name="pricing_mode" checked={pricingMode === o.v} onChange={() => setPricingMode(o.v)} className="rounded" />
                {o.l}
              </label>
            ))}
          </div>
          {pricingMode === 'fixed' && (
            <div className="mt-3">
              <label className="text-xs text-gray-500 block mb-1">Fixed Price (£ ex-VAT)</label>
              <input type="number" step="1" value={fixedPrice} onChange={e => setFixedPrice(e.target.value ? parseFloat(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
          )}
          {pricingMode === 'per_mile' && (
            <div className="mt-3">
              <label className="text-xs text-gray-500 block mb-1">Price per Mile (£)</label>
              <input type="number" step="0.01" value={perMilePrice} onChange={e => setPerMilePrice(e.target.value ? parseFloat(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
          )}
          {pricingMode === 'per_hour' && (
            <div className="mt-3">
              <label className="text-xs text-gray-500 block mb-1">Price per Hour (£)</label>
              <input type="number" step="0.01" value={perHourPrice} onChange={e => setPerHourPrice(e.target.value ? parseFloat(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
          )}
        </div>

        {/* Quote Validity & Terms */}
        <div className="bg-white rounded border border-gray-200 p-4 space-y-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Quote Valid For (days)</label>
            <input type="number" min="1" value={quoteValidityDays} onChange={e => setQuoteValidityDays(e.target.value ? parseInt(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Terms & Conditions (optional)</label>
            <textarea placeholder="Add custom T&Cs for this quote..." value={customTermsAndConditions} onChange={e => setCustomTermsAndConditions(e.target.value)}
              rows={3} className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-600" />
          </div>
        </div>

        {/* Cost Inputs — these are the PRIMARY pricing inputs for cost-plus mode */}
        {pricingMode === 'cost_plus' && (
          <div className="bg-white rounded border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Cost & Pricing Inputs</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Vehicle Type</label>
              <select value={vehicleType} onChange={e => setVehicleType(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                <option value="hgv">HGV</option><option value="van">Van</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">MPG</label>
              <input type="number" step="0.5" value={mpg} onChange={e => setMpg(e.target.value ? parseFloat(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Fuel Price (p/L ex-VAT)</label>
              <input type="number" step="0.01" value={fuelPrice} onChange={e => setFuelPrice(e.target.value ? parseFloat(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Driver Rate (£/hr)</label>
              <input type="number" step="0.5" value={driverRate} onChange={e => setDriverRate(e.target.value ? parseFloat(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Target Margin (%)</label>
              <input type="number" step="0.5" value={marginPct} onChange={e => setMarginPct(e.target.value ? parseFloat(e.target.value) : '')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Running Cost (£/mile)</label>
              <input type="number" step="0.01" value={runningCostPerMile} onChange={e => setRunningCostPerMile(e.target.value ? parseFloat(e.target.value) : '')}
                placeholder="Tyres, wear, depreciation" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Extra Costs (£)</label>
              <input type="number" step="1" value={extraCosts} onChange={e => setExtraCosts(e.target.value ? parseFloat(e.target.value) : '')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
          </div>
          {(extraCosts as number) > 0 && (
            <input placeholder="Extra costs description (e.g. tolls, ferries)" value={extraDesc} onChange={e => setExtraDesc(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm mt-3" />
          )}
          <label className="flex items-center gap-2 mt-3 text-sm text-gray-600">
            <input type="checkbox" checked={includeVat} onChange={e => setIncludeVat(e.target.checked)} className="rounded" />
            Include VAT (20%)
          </label>
          </div>
        )}

        <button onClick={calculate} disabled={calculating}
          className="w-full py-3 bg-slate-700 text-white font-semibold rounded hover:bg-slate-800 disabled:opacity-50 transition-colors">
          {calculating ? 'Calculating route...' : 'Calculate Quote'}
        </button>
        {error && <p className="text-red-600 text-sm bg-red-50 p-3 rounded">{error}</p>}
      </div>

      {/* ── RIGHT: Map + Results ── */}
      <div className="space-y-4">
        {/* Map */}
        <div className="bg-white rounded border border-gray-200 overflow-hidden">
          <div ref={mapRef} style={{ height: 300 }} className="bg-gray-100" />
          {!result && (
            <div className="flex items-center justify-center" style={{ marginTop: -160, height: 0, position: 'relative' }}>
              <p className="text-gray-400 text-sm pointer-events-none">Enter stops and calculate to see route</p>
            </div>
          )}
        </div>

        {result && c && (
          <>
            {/* Journey Summary */}
            <div className="bg-white rounded border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Journey Summary</h3>
              {mode === 'coach' && (passengerCount || wheelchairAccessible) && (
                <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b">
                  {passengerCount && <div><p className="text-sm font-semibold text-gray-700">{passengerCount}</p><p className="text-xs text-gray-500">Passengers</p></div>}
                  {wheelchairAccessible && <div><p className="text-sm font-semibold text-gray-700">{wheelchairPositions || '—'}</p><p className="text-xs text-gray-500">Wheelchair Spaces</p></div>}
                </div>
              )}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div><p className="text-sm font-semibold uppercase tracking-wide">{fmtMiles(result.summary.total_distance_miles)}</p><p className="text-xs text-gray-500">Distance</p></div>
                <div><p className="text-sm font-semibold uppercase tracking-wide">{fmtHours(result.summary.total_driving_hours)}</p><p className="text-xs text-gray-500">Driving</p></div>
                <div><p className="text-sm font-semibold uppercase tracking-wide">{result.summary.total_days} day{result.summary.total_days > 1 ? 's' : ''}</p>
                  <p className="text-xs text-gray-500">{result.summary.overnight_count > 0 ? `${result.summary.overnight_count} overnight${result.summary.overnight_count > 1 ? 's' : ''}` : 'Same day'}</p></div>
              </div>
              {!result.has_ors_route && (
                <p className="text-xs text-amber-600 mt-2 text-center">
                  {result.ors_key_configured ? 'Route estimation used (ORS returned no geometry)' : 'Estimated distances — set OPENROUTESERVICE_API_KEY for HGV routing'}
                </p>
              )}
            </div>

            {/* ═══ QUOTE PRICE — varies by pricing mode ═══ */}
            <div className="bg-slate-700 text-white rounded p-4">
              <h3 className="text-sm font-semibold mb-3 opacity-90">Quote Price</h3>
              <div className="space-y-2 text-sm">
                {pricingMode === 'cost_plus' && (
                  <>
                    <div className="flex justify-between">
                      <span className="opacity-80">Transport charge (costs + {c.margin_pct}% margin)</span>
                      <span>{fmt(c.base_transport_charge)}</span>
                    </div>
                    {c.fuel_surcharge_pct > 0 && (
                      <div className="flex justify-between">
                        <span className="opacity-80">Fuel surcharge ({c.fuel_surcharge_pct}%)</span>
                        <span>{fmt(c.fuel_surcharge_amount)}</span>
                      </div>
                    )}
                  </>
                )}
                {pricingMode === 'fixed' && (
                  <div className="flex justify-between">
                    <span className="opacity-80">Fixed quote price</span>
                    <span>{fmt(fixedPrice as number)}</span>
                  </div>
                )}
                {pricingMode === 'per_mile' && result.summary.total_distance_miles && (
                  <div className="flex justify-between">
                    <span className="opacity-80">Per mile ({fmtMiles(result.summary.total_distance_miles)} × £{(perMilePrice as number).toFixed(2)})</span>
                    <span>{fmt((result.summary.total_distance_miles * (perMilePrice as number)))}</span>
                  </div>
                )}
                {pricingMode === 'per_hour' && result.summary.total_driving_hours && (
                  <div className="flex justify-between">
                    <span className="opacity-80">Per hour ({fmtHours(result.summary.total_driving_hours)} × £{(perHourPrice as number).toFixed(2)})</span>
                    <span>{fmt((result.summary.total_driving_hours * (perHourPrice as number)))}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-white/20 pt-2 text-sm font-semibold">
                  <span>Total ex-VAT</span>
                  <span>
                    {pricingMode === 'fixed' ? fmt(fixedPrice as number)
                    : pricingMode === 'per_mile' ? fmt((result.summary.total_distance_miles || 0) * (perMilePrice as number))
                    : pricingMode === 'per_hour' ? fmt((result.summary.total_driving_hours || 0) * (perHourPrice as number))
                    : fmt(c.quote_ex_vat)}
                  </span>
                </div>
                {includeVat && (
                  <>
                    <div className="flex justify-between opacity-70"><span>VAT (20%)</span><span>{fmt(c.vat_amount)}</span></div>
                    <div className="flex justify-between text-sm font-semibold"><span>Total inc. VAT</span><span>{fmt(c.total_price)}</span></div>
                  </>
                )}
              </div>
            </div>

            {/* ═══ COST BREAKDOWN — what it actually costs you ═══ */}
            <div className="bg-white rounded border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Cost Breakdown</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Fuel ({c.fuel_litres}L at {c.fuel_pence_ex_vat}p/L)</span>
                  <span>{fmt(c.fuel_cost_at_current)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Driver ({fmtHours(result.summary.total_duty_hours)} at £{result.inputs.driver_rate_per_hour}/hr)</span>
                  <span>{fmt(c.driver_cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Vehicle running ({result.summary.total_distance_miles?.toFixed(0)} mi at £{c.running_cost_per_mile}/mi)</span>
                  <span>{fmt(c.running_cost)}</span>
                </div>
                {c.overnight_count > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Overnight ({c.overnight_count} night{c.overnight_count > 1 ? 's' : ''})</span>
                    <span>{fmt(c.overnight_cost)}</span>
                  </div>
                )}
                {c.extra_costs > 0 && (
                  <div className="flex justify-between"><span className="text-gray-600">Extra costs</span><span>{fmt(c.extra_costs)}</span></div>
                )}
                <div className="flex justify-between border-t pt-2 font-medium"><span>Total Cost</span><span>{fmt(c.actual_total_cost)}</span></div>
                <div className="flex justify-between font-medium"><span>Margin ({c.margin_pct}% on selling price)</span><span>{fmt(c.margin_amount)}</span></div>
                <div className={`flex justify-between border-t pt-2 font-bold text-lg ${c.actual_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <span>Actual Margin</span><span>{fmt(c.actual_margin)} ({c.actual_margin_pct}%)</span>
                </div>
              </div>
            </div>

            {/* ═══ RATE CARD BENCHMARK — for comparison ═══ */}
            {bench?.available && (
              <div className="bg-gray-50 rounded border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Rate Card Benchmark</h3>
                  {variance && (
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      variance.difference_pct >= 0
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {variance.difference_pct >= 0 ? '+' : ''}{variance.difference_pct}% vs rate card
                    </span>
                  )}
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <div className="flex justify-between"><span>Base rate ({serviceSubtype.replace(/_/g, ' ')})</span><span>{fmt(bench.base)}</span></div>
                  {bench.waiting_surcharge > 0 && <div className="flex justify-between"><span>Waiting surcharge</span><span>{fmt(bench.waiting_surcharge)}</span></div>}
                  {bench.weekend_surcharge > 0 && <div className="flex justify-between"><span>Weekend surcharge</span><span>{fmt(bench.weekend_surcharge)}</span></div>}
                  {bench.fuel_surcharge > 0 && <div className="flex justify-between"><span>Fuel surcharge</span><span>{fmt(bench.fuel_surcharge)}</span></div>}
                  <div className="flex justify-between border-t pt-1 font-medium text-gray-700">
                    <span>Rate card total</span><span>{fmt(bench.total)}</span>
                  </div>
                  {variance && (
                    <div className="flex justify-between font-medium">
                      <span>Your quote</span><span>{fmt(variance.calculator_price)}</span>
                    </div>
                  )}
                  {variance && (
                    <p className={`text-xs mt-1 ${variance.difference_pct >= -5 && variance.difference_pct <= 15 ? 'text-green-600' : 'text-amber-600'}`}>
                      {variance.difference_pct > 0
                        ? `Your quote is ${fmt(variance.difference)} above published rates — premium pricing`
                        : variance.difference_pct < -5
                          ? `Your quote is ${fmt(Math.abs(variance.difference))} below published rates — consider reviewing`
                          : 'Your quote is closely aligned with published rates'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Journey Plan */}
            <details className="bg-white rounded border border-gray-200">
              <summary className="p-4 text-sm font-semibold text-gray-700 cursor-pointer">Journey Plan (EC 561 + WTD)</summary>
              <div className="px-4 pb-4 space-y-1 max-h-64 overflow-y-auto">
                {result.journey_plan.timeline.map((ev: any, i: number) => {
                  const colors: Record<string, string> = {
                    driving: 'bg-blue-100 text-blue-700', break: 'bg-amber-100 text-amber-700',
                    waiting: 'bg-gray-100 text-gray-600', rest: 'bg-purple-100 text-purple-700',
                  };
                  return (
                    <div key={i} className={`flex items-center gap-3 px-3 py-1.5 rounded text-xs ${colors[ev.type] || 'bg-gray-50'}`}>
                      <span className="font-mono w-20 flex-shrink-0">Day {ev.day} {timeStr(ev.start_time)}</span>
                      <span className="flex-1">{ev.description}</span>
                      <span className="font-mono">{fmtHours(ev.duration)}</span>
                    </div>
                  );
                })}
              </div>
            </details>

            {/* Route legs */}
            <details className="bg-white rounded border border-gray-200">
              <summary className="p-4 text-sm font-semibold text-gray-700 cursor-pointer">Route Legs</summary>
              <div className="px-4 pb-4 space-y-2">
                {result.legs.map((leg: any, i: number) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0">
                    <span className="text-gray-600">{result.stops[leg.from_stop]?.label} → {result.stops[leg.to_stop]?.label}</span>
                    <span className="font-medium">{leg.distance_miles.toFixed(1)} mi · {fmtHours(leg.duration_hours)}</span>
                  </div>
                ))}
              </div>
            </details>

            {/* Save */}
            <div className="flex gap-3">
              <button onClick={saveQuote} disabled={saving}
                className="flex-1 py-3 bg-slate-700 text-white font-semibold rounded hover:bg-slate-800 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Quote'}
              </button>
            </div>
            {saveMsg && <p className={`text-sm p-2 rounded ${saveMsg.startsWith('Error') ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50'}`}>{saveMsg}</p>}
          </>
        )}
      </div>

      {showCustomerModal && (
        <CustomerModal onClose={() => setShowCustomerModal(false)} onSave={async (c) => {
          const custs = await api.getCustomers(); setCustomers(custs); setCustomerId(c.id); setShowCustomerModal(false);
        }} />
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   CUSTOMER MODAL — expanded with accounts/invoicing contacts
   ═══════════════════════════════════════════════════════════════════ */

function CustomerModal({ onClose, onSave, existing }: { onClose: () => void; onSave: (c: any) => void; existing?: Customer }) {
  const [form, setForm] = useState({
    customer_name: existing?.customer_name || '',
    contact_name: existing?.contact_name || '',
    email: existing?.email || '', phone: existing?.phone || '',
    address: existing?.address || '', postcode: existing?.postcode || '',
    notes: existing?.notes || '',
    accounts_contact_name: existing?.accounts_contact_name || '',
    accounts_contact_email: existing?.accounts_contact_email || '',
    accounts_contact_phone: existing?.accounts_contact_phone || '',
    invoicing_contact_name: existing?.invoicing_contact_name || '',
    invoicing_contact_email: existing?.invoicing_contact_email || '',
    invoicing_contact_phone: existing?.invoicing_contact_phone || '',
    credit_terms_days: existing?.credit_terms_days || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const u = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.customer_name.trim()) { setErr('Customer name required'); return; }
    setSaving(true);
    try {
      const data = { ...form, credit_terms_days: form.credit_terms_days ? Number(form.credit_terms_days) : null };
      const res = existing ? await api.updateCustomer(existing.id, data) : await api.createCustomer(data);
      onSave(res);
    } catch (e) { setErr(errorMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold mb-4">{existing ? 'Edit' : 'New'} Customer</h3>
        <div className="space-y-3">
          <input placeholder="Company / Customer name *" value={form.customer_name} onChange={e => u('customer_name', e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Main contact" value={form.contact_name} onChange={e => u('contact_name', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Email" value={form.email} onChange={e => u('email', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Phone" value={form.phone} onChange={e => u('phone', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Postcode" value={form.postcode} onChange={e => u('postcode', e.target.value.toUpperCase())} className="border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <input placeholder="Address" value={form.address} onChange={e => u('address', e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />

          <p className="text-xs font-semibold text-gray-500 mt-2">Accounts Contact</p>
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="Name" value={form.accounts_contact_name} onChange={e => u('accounts_contact_name', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Email" value={form.accounts_contact_email} onChange={e => u('accounts_contact_email', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Phone" value={form.accounts_contact_phone} onChange={e => u('accounts_contact_phone', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>

          <p className="text-xs font-semibold text-gray-500">Invoicing Contact</p>
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="Name" value={form.invoicing_contact_name} onChange={e => u('invoicing_contact_name', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Email" value={form.invoicing_contact_email} onChange={e => u('invoicing_contact_email', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
            <input placeholder="Phone" value={form.invoicing_contact_phone} onChange={e => u('invoicing_contact_phone', e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Credit Terms (days)</label>
              <input type="number" placeholder="e.g. 30" value={form.credit_terms_days} onChange={e => u('credit_terms_days', e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
          </div>
          <textarea placeholder="Notes" value={form.notes} onChange={e => u('notes', e.target.value)} rows={2} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
        </div>
        {err && <p className="text-red-600 text-sm mt-2">{err}</p>}
        <div className="flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-800 disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   QUOTES LIST TAB — with click-to-view detail
   ═══════════════════════════════════════════════════════════════════ */

function QuotesTab() {
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionMsg, setActionMsg] = useState('');
  const [selectedQuote, setSelectedQuote] = useState<any | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const mapDetailRef = useRef<HTMLDivElement>(null);
  const mapDetailInstance = useRef<any>(null);

  const load = async () => {
    setLoading(true);
    try { const res = await api.getQuotes({ status: statusFilter || undefined, page, page_size: 25 }); setQuotes(res.items); setTotal(res.total); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [statusFilter, page]);

  const openDetail = async (id: number) => {
    setLoadingDetail(true);
    try {
      const full = await api.getQuote(id);
      setSelectedQuote(full);
      // Render map after state update
      setTimeout(() => renderDetailMap(full), 100);
    } catch (e) { setActionMsg(`Error loading quote: ${errorMsg(e)}`); }
    setLoadingDetail(false);
  };

  const renderDetailMap = async (q: any) => {
    if (!q.route_geometry && !q.stops?.length) return;
    await loadLeaflet();
    const L = (window as any).L;
    if (!L || !mapDetailRef.current) return;
    if (mapDetailInstance.current) { mapDetailInstance.current.remove(); mapDetailInstance.current = null; }
    const map = L.map(mapDetailRef.current).setView([54.5, -2], 6);
    mapDetailInstance.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);
    const bounds: any[] = [];
    if (q.stops) {
      q.stops.forEach((s: any, i: number) => {
        if (!s.latitude || !s.longitude) return;
        const color = i === 0 ? '#16a34a' : i === q.stops.length - 1 ? '#dc2626' : '#2563eb';
        L.circleMarker([s.latitude, s.longitude], {
          radius: 8, fillColor: color, color: '#fff', weight: 2, fillOpacity: 0.9,
        }).addTo(map).bindPopup(`<b>${s.label || `Stop ${i + 1}`}</b><br/>${s.postcode || ''}`);
        bounds.push([s.latitude, s.longitude]);
      });
    }
    if (q.route_geometry?.length > 0) L.polyline(q.route_geometry, { color: '#4f46e5', weight: 4, opacity: 0.7 }).addTo(map);
    if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
  };

  const doAction = async (id: number, action: 'approve' | 'decline' | 'send') => {
    setActionMsg('');
    try {
      if (action === 'approve') { const r = await api.approveQuote(id); setActionMsg(`Approved — created job ${r.job_number}`); }
      else if (action === 'decline') { await api.declineQuote(id); setActionMsg('Declined'); }
      else { await api.sendQuote(id); setActionMsg('Marked as sent'); }
      load();
      if (selectedQuote?.id === id) openDetail(id); // refresh detail view
    } catch (e) { setActionMsg(`Error: ${errorMsg(e)}`); }
  };

  // ── DETAIL VIEW ──
  if (selectedQuote) {
    const q = selectedQuote;
    const jp = q.journey_plan;
    return (
      <div>
        <button onClick={() => { setSelectedQuote(null); if (mapDetailInstance.current) { mapDetailInstance.current.remove(); mapDetailInstance.current = null; } }}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mb-4">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          Back to quotes
        </button>

        {actionMsg && <p className={`text-sm p-2 rounded mb-3 ${actionMsg.startsWith('Error') ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50'}`}>{actionMsg}</p>}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-900">{q.quote_number}</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[q.status] || 'bg-gray-100'}`}>{q.status}</span>
              <span className="text-sm text-gray-500">{q.quote_date}</span>
              {q.valid_until && <span className="text-sm text-gray-400">Valid until {q.valid_until}</span>}
              {q.customer && <span className="text-sm font-medium text-gray-700">{q.customer.customer_name}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            {(q.status === 'draft' || q.status === 'sent') && (
              <>
                <button onClick={() => doAction(q.id, 'approve')} className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700">Approve</button>
                <button onClick={() => doAction(q.id, 'decline')} className="px-4 py-2 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200">Decline</button>
              </>
            )}
            {q.status === 'draft' && (
              <button onClick={() => doAction(q.id, 'send')} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">Mark Sent</button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* LEFT: Map + Journey Summary */}
          <div className="space-y-4">
            {/* Map */}
            <div className="bg-white rounded border border-gray-200 overflow-hidden">
              <div ref={mapDetailRef} style={{ height: 300 }} className="bg-gray-100" />
            </div>

            {/* Journey Summary */}
            <div className="bg-white rounded border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Journey Summary</h3>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div><p className="text-sm font-semibold uppercase tracking-wide">{q.total_distance_miles ? `${q.total_distance_miles.toFixed(0)} mi` : '—'}</p><p className="text-xs text-gray-500">Distance</p></div>
                <div><p className="text-sm font-semibold uppercase tracking-wide">{fmtHours(q.total_driving_hours)}</p><p className="text-xs text-gray-500">Driving</p></div>
                <div><p className="text-sm font-semibold uppercase tracking-wide">{fmtHours(q.total_duty_hours)}</p><p className="text-xs text-gray-500">Duty</p></div>
                <div><p className="text-sm font-semibold uppercase tracking-wide">{q.overnight_count || 0}</p><p className="text-xs text-gray-500">Overnights</p></div>
              </div>
            </div>

            {/* Stops */}
            {q.stops && q.stops.length > 0 && (
              <div className="bg-white rounded border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Route Stops</h3>
                <div className="space-y-2">
                  {q.stops.map((s: any, i: number) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <div className={`w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${
                        i === 0 ? 'bg-green-500' : i === q.stops.length - 1 ? 'bg-red-500' : 'bg-blue-500'
                      }`}>{i + 1}</div>
                      <div className="flex-1">
                        <span className="font-medium">{s.label || `Stop ${i + 1}`}</span>
                        {s.postcode && <span className="text-gray-400 ml-2 font-mono text-xs">{s.postcode}</span>}
                        {s.address && <span className="text-gray-400 ml-2 text-xs">{s.address}</span>}
                      </div>
                      {s.waiting_time_hours > 0 && (
                        <span className="text-xs text-gray-400">{s.waiting_time_hours}h wait</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Journey Plan */}
            {jp?.timeline && jp.timeline.length > 0 && (
              <details className="bg-white rounded border border-gray-200" open>
                <summary className="p-4 text-sm font-semibold text-gray-700 cursor-pointer">Journey Plan (EC 561 + WTD)</summary>
                <div className="px-4 pb-4 space-y-1 max-h-80 overflow-y-auto">
                  {jp.timeline.map((ev: any, i: number) => {
                    const colors: Record<string, string> = {
                      driving: 'bg-blue-100 text-blue-700', break: 'bg-amber-100 text-amber-700',
                      waiting: 'bg-gray-100 text-gray-600', rest: 'bg-purple-100 text-purple-700',
                    };
                    return (
                      <div key={i} className={`flex items-center gap-3 px-3 py-1.5 rounded text-xs ${colors[ev.type] || 'bg-gray-50'}`}>
                        <span className="font-mono w-20 flex-shrink-0">Day {ev.day} {timeStr(ev.start_time)}</span>
                        <span className="flex-1">{ev.description}</span>
                        <span className="font-mono">{fmtHours(ev.duration)}</span>
                      </div>
                    );
                  })}
                </div>
              </details>
            )}
          </div>

          {/* RIGHT: Pricing + Cost Breakdown */}
          <div className="space-y-4">
            {/* Quote Price */}
            <div className="bg-slate-700 text-white rounded p-4">
              <h3 className="text-sm font-semibold mb-3 opacity-90">Quote Price</h3>
              <div className="space-y-2 text-sm">
                {q.subtotal != null && (
                  <div className="flex justify-between"><span className="opacity-80">Base costs</span><span>{fmt(q.subtotal)}</span></div>
                )}
                {q.margin_amount != null && (
                  <div className="flex justify-between"><span className="opacity-80">Margin ({q.profit_margin_pct || '—'}%)</span><span>{fmt(q.margin_amount)}</span></div>
                )}
                {q.fuel_surcharge_pct > 0 && (
                  <div className="flex justify-between"><span className="opacity-80">Fuel surcharge ({q.fuel_surcharge_pct}%)</span><span>{fmt(q.fuel_surcharge_amount)}</span></div>
                )}
                <div className="flex justify-between border-t border-white/20 pt-2 text-sm font-semibold">
                  <span>Total ex-VAT</span>
                  <span>{fmt((q.total_price || 0) - (q.vat_amount || 0))}</span>
                </div>
                {q.include_vat && q.vat_amount > 0 && (
                  <>
                    <div className="flex justify-between opacity-70"><span>VAT (20%)</span><span>{fmt(q.vat_amount)}</span></div>
                    <div className="flex justify-between text-sm font-semibold"><span>Total inc. VAT</span><span>{fmt(q.total_price)}</span></div>
                  </>
                )}
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="bg-white rounded border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Cost Breakdown</h3>
              <div className="space-y-2 text-sm">
                {q.fuel_cost != null && <div className="flex justify-between"><span className="text-gray-600">Fuel</span><span>{fmt(q.fuel_cost)}</span></div>}
                {q.driver_cost != null && <div className="flex justify-between"><span className="text-gray-600">Driver</span><span>{fmt(q.driver_cost)}</span></div>}
                {q.running_cost != null && <div className="flex justify-between"><span className="text-gray-600">Vehicle running</span><span>{fmt(q.running_cost)}</span></div>}
                {q.overnight_cost > 0 && <div className="flex justify-between"><span className="text-gray-600">Overnight ({q.overnight_count} night{q.overnight_count > 1 ? 's' : ''})</span><span>{fmt(q.overnight_cost)}</span></div>}
                {q.extra_costs > 0 && <div className="flex justify-between"><span className="text-gray-600">Extra costs</span><span>{fmt(q.extra_costs)}{q.extra_costs_description ? ` — ${q.extra_costs_description}` : ''}</span></div>}
              </div>
            </div>

            {/* Quote Parameters */}
            <div className="bg-white rounded border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Quote Parameters</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-medium">{(q.vehicle_type || 'hgv').toUpperCase()}</span></div>
                {q.mpg && <div className="flex justify-between"><span className="text-gray-500">MPG</span><span className="font-medium">{q.mpg}</span></div>}
                {q.fuel_price_per_litre && <div className="flex justify-between"><span className="text-gray-500">Fuel price</span><span className="font-medium">{q.fuel_price_per_litre}p/L</span></div>}
                {q.driver_rate_per_hour && <div className="flex justify-between"><span className="text-gray-500">Driver rate</span><span className="font-medium">£{q.driver_rate_per_hour}/hr</span></div>}
                {q.profit_margin_pct != null && <div className="flex justify-between"><span className="text-gray-500">Margin</span><span className="font-medium">{q.profit_margin_pct}%</span></div>}
                {q.running_cost_per_mile != null && <div className="flex justify-between"><span className="text-gray-500">Running cost</span><span className="font-medium">£{q.running_cost_per_mile}/mi</span></div>}
              </div>
              {/* Coach-specific parameters */}
              {q.mode === 'coach' && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-3">Coach/PSV Details</h4>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {q.coach_service_type && <div className="flex justify-between"><span className="text-gray-500">Service Type</span><span className="font-medium capitalize">{q.coach_service_type.replace(/_/g, ' ')}</span></div>}
                    {q.passenger_count && <div className="flex justify-between"><span className="text-gray-500">Passengers</span><span className="font-medium">{q.passenger_count}</span></div>}
                    {q.wheelchair_accessible !== undefined && <div className="flex justify-between"><span className="text-gray-500">Wheelchair Access</span><span className="font-medium">{q.wheelchair_accessible ? 'Yes' : 'No'}</span></div>}
                    {q.wheelchair_positions && <div className="flex justify-between"><span className="text-gray-500">Wheelchair Spaces</span><span className="font-medium">{q.wheelchair_positions}</span></div>}
                  </div>
                </div>
              )}
            </div>

            {/* Professional Quote Preview */}
            <details className="bg-white rounded border border-gray-200">
              <summary className="p-4 text-sm font-semibold text-gray-700 cursor-pointer">Professional Quote Document</summary>
              <div className="px-4 pb-6 border-t max-h-96 overflow-y-auto">
                <div className="bg-gray-50 p-6 rounded mt-4 text-sm leading-relaxed">
                  <div className="border-b pb-4 mb-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-1">QUOTATION</h4>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div><span className="text-gray-500">Quote Number:</span> <span className="font-mono font-bold">{q.quote_number}</span></div>
                      <div><span className="text-gray-500">Date:</span> <span>{q.quote_date}</span></div>
                      {q.valid_until && <div><span className="text-gray-500">Valid Until:</span> <span>{q.valid_until}</span></div>}
                    </div>
                  </div>

                  {q.customer && (
                    <div className="border-b pb-4 mb-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-2">Bill To</h4>
                      <p className="font-medium">{q.customer.customer_name}</p>
                      {q.customer.contact_name && <p className="text-gray-600">{q.customer.contact_name}</p>}
                      {q.customer.address && <p className="text-gray-600">{q.customer.address}</p>}
                      {q.customer.postcode && <p className="text-gray-600 font-mono">{q.customer.postcode}</p>}
                    </div>
                  )}

                  {/* Summary */}
                  <div className="border-b pb-4 mb-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-2">Service Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div><span className="text-gray-500">Distance:</span> <span>{q.total_distance_miles ? q.total_distance_miles.toFixed(0) : '—'} miles</span></div>
                      <div><span className="text-gray-500">Duration:</span> <span>{fmtHours(q.total_driving_hours)}</span></div>
                      {q.mode === 'coach' && q.passenger_count && <div><span className="text-gray-500">Passengers:</span> <span>{q.passenger_count}</span></div>}
                    </div>
                  </div>

                  {/* Price Summary */}
                  <div className="bg-white p-3 rounded border border-gray-200">
                    <div className="flex justify-between text-xs mb-2"><span className="text-gray-600">Subtotal</span><span>{fmt(q.subtotal)}</span></div>
                    {q.margin_amount && <div className="flex justify-between text-xs mb-2"><span className="text-gray-600">Margin</span><span>{fmt(q.margin_amount)}</span></div>}
                    <div className="flex justify-between border-t pt-2 font-medium"><span>Total ex-VAT</span><span>{fmt((q.total_price || 0) - (q.vat_amount || 0))}</span></div>
                    {q.include_vat && q.vat_amount && (
                      <>
                        <div className="flex justify-between text-xs text-gray-600 mt-1"><span>VAT (20%)</span><span>{fmt(q.vat_amount)}</span></div>
                        <div className="flex justify-between font-bold text-sm mt-2 pt-2 border-t"><span>TOTAL</span><span>{fmt(q.total_price)}</span></div>
                      </>
                    )}
                  </div>

                  {q.custom_terms && (
                    <div className="mt-4 pt-4 border-t text-xs">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-2">Terms & Conditions</h4>
                      <p className="text-gray-600 whitespace-pre-wrap">{q.custom_terms}</p>
                    </div>
                  )}

                  <div className="mt-4 text-xs text-gray-500 text-center">
                    <p>This quotation is valid for {q.quote_validity_days || 14} days</p>
                  </div>
                </div>
              </div>
            </details>

            {/* Notes */}
            {q.notes && (
              <div className="bg-white rounded border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
                <p className="text-sm text-gray-600">{q.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="border border-gray-300 rounded px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {['draft', 'sent', 'accepted', 'declined', 'converted', 'expired'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <span className="text-sm text-gray-500">{total} quote{total !== 1 ? 's' : ''}</span>
      </div>
      {actionMsg && <p className={`text-sm p-2 rounded mb-3 ${actionMsg.startsWith('Error') ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50'}`}>{actionMsg}</p>}
      <div className="bg-white rounded border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-4 py-3 font-medium">Quote #</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Distance</th>
              <th className="px-4 py-3 font-medium text-right">Price</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            : quotes.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No quotes yet</td></tr>
            : quotes.map(q => (
              <tr key={q.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(q.id)}>
                <td className="px-4 py-3 font-mono font-medium text-blue-600">{q.quote_number}</td>
                <td className="px-4 py-3">{q.customer?.customer_name || '—'}</td>
                <td className="px-4 py-3 text-gray-500">{q.quote_date}</td>
                <td className="px-4 py-3">{q.total_distance_miles ? `${q.total_distance_miles.toFixed(0)} mi` : '—'}</td>
                <td className="px-4 py-3 text-right font-medium">{fmt(q.total_price)}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[q.status] || 'bg-gray-100'}`}>{q.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    {(q.status === 'draft' || q.status === 'sent') && (
                      <>
                        <button onClick={() => doAction(q.id, 'approve')} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200">Approve</button>
                        <button onClick={() => doAction(q.id, 'decline')} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200">Decline</button>
                      </>
                    )}
                    {q.status === 'draft' && <button onClick={() => doAction(q.id, 'send')} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">Send</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 25 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-40">Prev</button>
          <span className="text-sm text-gray-600">Page {page} of {Math.ceil(total / 25)}</span>
          <button disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-40">Next</button>
        </div>
      )}
      {loadingDetail && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded p-6 shadow-lg"><p className="text-gray-600">Loading quote...</p></div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   CUSTOMERS TAB — with detail panel
   ═══════════════════════════════════════════════════════════════════ */

function CustomersTab() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [detailCustomer, setDetailCustomer] = useState<Customer | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try { setCustomers(await api.getCustomers()); } catch {} setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const loadDetail = async (id: number) => {
    try {
      const c = await api.getCustomerDetail(id);
      setDetailCustomer(c);
    } catch {}
  };

  const filtered = customers.filter(c =>
    !search || c.customer_name.toLowerCase().includes(search.toLowerCase()) ||
    (c.contact_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <div className="flex items-center gap-3 mb-4">
          <input placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm w-64" />
          <button onClick={() => { setEditCustomer(null); setShowModal(true); }}
            className="px-4 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-800">+ Add Customer</button>
        </div>
        <div className="bg-white rounded border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Credit Terms</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No customers</td></tr>
              : filtered.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => loadDetail(c.id)}>
                  <td className="px-4 py-3 font-medium">{c.customer_name}</td>
                  <td className="px-4 py-3 text-gray-600">{c.contact_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.credit_terms_days ? `${c.credit_terms_days} days` : '—'}</td>
                  <td className="px-4 py-3">
                    <button onClick={(e) => { e.stopPropagation(); setEditCustomer(c); setShowModal(true); }}
                      className="text-blue-600 hover:underline text-xs">Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel */}
      <div>
        {detailCustomer ? (
          <div className="bg-white rounded border border-gray-200 p-4 sticky top-6">
            <h3 className="font-semibold text-gray-900 mb-3">{detailCustomer.customer_name}</h3>
            <div className="space-y-3 text-sm">
              <div><span className="text-gray-500">Main Contact:</span> <span>{detailCustomer.contact_name || '—'}</span></div>
              <div><span className="text-gray-500">Email:</span> <span>{detailCustomer.email || '—'}</span></div>
              <div><span className="text-gray-500">Phone:</span> <span>{detailCustomer.phone || '—'}</span></div>
              {detailCustomer.accounts_contact_name && (
                <div className="border-t pt-2">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Accounts Contact</p>
                  <div>{detailCustomer.accounts_contact_name} · {detailCustomer.accounts_contact_email} · {detailCustomer.accounts_contact_phone}</div>
                </div>
              )}
              {detailCustomer.invoicing_contact_name && (
                <div className="border-t pt-2">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Invoicing Contact</p>
                  <div>{detailCustomer.invoicing_contact_name} · {detailCustomer.invoicing_contact_email} · {detailCustomer.invoicing_contact_phone}</div>
                </div>
              )}
              <div className="border-t pt-2"><span className="text-gray-500">Credit Terms:</span> <span>{detailCustomer.credit_terms_days ? `${detailCustomer.credit_terms_days} days` : 'Not set'}</span></div>
              <div className="grid grid-cols-2 gap-3 border-t pt-3">
                <div className="bg-green-50 rounded p-3 text-center">
                  <p className="text-sm font-semibold text-green-700">{fmt(detailCustomer.total_spend)}</p>
                  <p className="text-xs text-green-600">All-Time Spend</p>
                </div>
                <div className="bg-blue-50 rounded p-3 text-center">
                  <p className="text-sm font-semibold text-blue-700">{detailCustomer.job_count}</p>
                  <p className="text-xs text-blue-600">Total Jobs</p>
                </div>
                <div className="bg-amber-50 rounded p-3 text-center">
                  <p className="text-sm font-semibold text-amber-700">{fmt(detailCustomer.outstanding_amount)}</p>
                  <p className="text-xs text-amber-600">Outstanding</p>
                </div>
                <div className="bg-gray-50 rounded p-3 text-center">
                  <p className="text-sm font-semibold text-gray-700">{detailCustomer.quote_count}</p>
                  <p className="text-xs text-gray-600">Quotes</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded border border-gray-200 p-8 text-center text-gray-400 text-sm">
            Click a customer to see details
          </div>
        )}
      </div>

      {showModal && (
        <CustomerModal existing={editCustomer || undefined} onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); load(); }} />
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   SETTINGS TAB — with rate card editor
   ═══════════════════════════════════════════════════════════════════ */

function SettingsTab() {
  const [config, setConfig] = useState<any>(null);
  const [rateCard, setRateCard] = useState<RateCard>(DEFAULT_RATE_CARD);
  const [fuelPriceInput, setFuelPriceInput] = useState('');
  const [fuelHistory, setFuelHistory] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [cfg, fp, hist] = await Promise.all([api.getQuoteConfig(), api.getLatestFuelPrice(), api.getFuelPriceHistory()]);
        setConfig(cfg);
        setFuelPriceInput(fp.price_per_litre?.toString() || '');
        setFuelHistory(hist);
        if (cfg.rate_card) setRateCard({ ...DEFAULT_RATE_CARD, ...cfg.rate_card });
      } catch {}
    })();
  }, []);

  const saveAll = async () => {
    if (!config) return;
    setSaving(true); setMsg('');
    try {
      await api.updateQuoteConfig({ ...config, rate_card_json: JSON.stringify(rateCard) });
      setMsg('Settings saved');
    } catch (e) { setMsg(`Error: ${errorMsg(e)}`); } setSaving(false);
  };

  const addFuel = async () => {
    if (!fuelPriceInput) return;
    try {
      await api.addFuelPrice({ price_per_litre: parseFloat(fuelPriceInput) });
      setFuelHistory(await api.getFuelPriceHistory());
      setMsg('Fuel price updated');
    } catch (e) { setMsg(`Error: ${errorMsg(e)}`); }
  };

  const rc = (k: keyof RateCard, v: number) => setRateCard(prev => ({ ...prev, [k]: v }));

  if (!config) return <p className="text-gray-400">Loading...</p>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General defaults */}
        <div className="bg-white rounded border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">General Defaults</h3>
          <div className="space-y-3">
            <div><label className="text-xs text-gray-500 block mb-1">Depot Postcode</label>
              <input value={config.depot_postcode} onChange={e => setConfig({ ...config, depot_postcode: e.target.value.toUpperCase() })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500 block mb-1">MPG (HGV)</label><input type="number" step="0.5" value={config.default_mpg_hgv} onChange={e => setConfig({ ...config, default_mpg_hgv: parseFloat(e.target.value) || 0 })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500 block mb-1">MPG (Van)</label><input type="number" step="0.5" value={config.default_mpg_van} onChange={e => setConfig({ ...config, default_mpg_van: parseFloat(e.target.value) || 0 })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Driver Rate (£/hr)</label><input type="number" step="0.5" value={config.default_driver_rate} onChange={e => setConfig({ ...config, default_driver_rate: parseFloat(e.target.value) || 0 })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Target Margin (%)</label><input type="number" step="0.5" value={config.default_profit_margin} onChange={e => setConfig({ ...config, default_profit_margin: parseFloat(e.target.value) || 27.5 })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Running Cost (£/mile)</label><input type="number" step="0.01" value={config.default_running_cost_per_mile} onChange={e => setConfig({ ...config, default_running_cost_per_mile: parseFloat(e.target.value) || 0.30 })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Journey Start Time</label><input type="number" step="0.5" min="0" max="23" value={config.start_hour} onChange={e => setConfig({ ...config, start_hour: parseFloat(e.target.value) || 6 })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Overnight Accom (£)</label><input type="number" step="5" value={config.overnight_accommodation} onChange={e => setConfig({ ...config, overnight_accommodation: parseFloat(e.target.value) || 0 })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Overnight Subsist (£)</label><input type="number" step="5" value={config.overnight_subsistence} onChange={e => setConfig({ ...config, overnight_subsistence: parseFloat(e.target.value) || 0 })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></div>
            </div>
            <div><label className="text-xs text-gray-500 block mb-1">Baseline Fuel (p/L ex-VAT) — the price baked into your published rates</label>
              <input type="number" step="1" value={config.baseline_fuel_pence || 120} onChange={e => setConfig({ ...config, baseline_fuel_pence: parseFloat(e.target.value) || 120 })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></div>
          </div>
        </div>

        {/* Fuel */}
        <div className="bg-white rounded border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Current Fuel Price (p/L ex-VAT)</h3>
          <div className="flex gap-2 mb-4">
            <input type="number" step="0.01" placeholder="Pence per litre (ex-VAT)" value={fuelPriceInput}
              onChange={e => setFuelPriceInput(e.target.value)} className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm" />
            <button onClick={addFuel} className="px-4 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-800">Update</button>
          </div>
          {fuelPriceInput && config.baseline_fuel_pence && parseFloat(fuelPriceInput) > config.baseline_fuel_pence && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
              <p className="text-sm font-medium text-amber-800">
                Fuel surcharge: {(((parseFloat(fuelPriceInput) - config.baseline_fuel_pence) / config.baseline_fuel_pence) * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Current {parseFloat(fuelPriceInput).toFixed(2)}p vs baseline {config.baseline_fuel_pence}p — surcharge will apply to quotes automatically
              </p>
            </div>
          )}
          <h4 className="text-xs text-gray-500 mb-2">Price History</h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {fuelHistory.length === 0 ? <p className="text-gray-400 text-sm">No prices recorded</p>
            : fuelHistory.map((f: any) => (
              <div key={f.id} className="flex justify-between text-sm py-1 border-b border-gray-100">
                <span className="text-gray-600">{f.effective_date}</span>
                <span className="font-medium">{f.price_per_litre.toFixed(2)}p/L ex-VAT</span>
                <span className="text-xs text-gray-400">{f.source}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rate Card (benchmark) */}
      <div className="bg-white rounded border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Rate Card (all prices ex-VAT)</h3>
        <p className="text-xs text-gray-400 mb-4">Your published/illustrative pricing — used as a benchmark when quoting, not the primary price</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Standard */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Standard Operations</p>
            <div className="space-y-2">
              <div><label className="text-xs text-gray-400">Tractor & Trailer (£/day)</label><input type="number" value={rateCard.tractor_trailer_day} onChange={e => rc('tractor_trailer_day', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
              <div><label className="text-xs text-gray-400">Power Only (£/day)</label><input type="number" value={rateCard.power_only_day} onChange={e => rc('power_only_day', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
              <div><label className="text-xs text-gray-400">Waiting (£/hr)</label><input type="number" value={rateCard.waiting_per_hour} onChange={e => rc('waiting_per_hour', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
              <div><label className="text-xs text-gray-400">Waiting included (hrs)</label><input type="number" value={rateCard.waiting_included_hours} onChange={e => rc('waiting_included_hours', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
              <div><label className="text-xs text-gray-400">Weekend surcharge (%)</label><input type="number" value={rateCard.weekend_surcharge_pct} onChange={e => rc('weekend_surcharge_pct', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
            </div>
          </div>
          {/* Touring */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Touring & Live Events</p>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-gray-400">Day low (£)</label><input type="number" value={rateCard.touring_day_low} onChange={e => rc('touring_day_low', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs text-gray-400">Day high (£)</label><input type="number" value={rateCard.touring_day_high} onChange={e => rc('touring_day_high', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-gray-400">Week low (£)</label><input type="number" value={rateCard.touring_week_low} onChange={e => rc('touring_week_low', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs text-gray-400">Week high (£)</label><input type="number" value={rateCard.touring_week_high} onChange={e => rc('touring_week_high', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-gray-400">O/night low (£)</label><input type="number" value={rateCard.touring_overnight_low} onChange={e => rc('touring_overnight_low', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
                <div><label className="text-xs text-gray-400">O/night high (£)</label><input type="number" value={rateCard.touring_overnight_high} onChange={e => rc('touring_overnight_high', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
              </div>
            </div>
          </div>
          {/* Drop & Collect */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Trailer Drop & Collect</p>
            <div className="space-y-2">
              <div><label className="text-xs text-gray-400">Trailer Drop (£)</label><input type="number" value={rateCard.drop_collect_drop} onChange={e => rc('drop_collect_drop', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
              <div><label className="text-xs text-gray-400">Next Day Collection (£)</label><input type="number" value={rateCard.drop_collect_collection} onChange={e => rc('drop_collect_collection', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
              <div><label className="text-xs text-gray-400">Stand Day (£/day)</label><input type="number" value={rateCard.drop_collect_stand_day} onChange={e => rc('drop_collect_stand_day', parseFloat(e.target.value) || 0)} className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm" /></div>
            </div>
          </div>
        </div>
      </div>

      <button onClick={saveAll} disabled={saving} className="px-6 py-2 bg-slate-700 text-white rounded text-sm hover:bg-slate-800 disabled:opacity-50">{saving ? 'Saving...' : 'Save All Settings'}</button>
      {msg && <p className={`text-sm ${msg.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</p>}
    </div>
  );
}
